import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, HandCoins, Heart, LogOut, Medal, RotateCcw } from 'lucide-react';
import { centerToast } from '../../components/CenterToast';
import TopTabStrip from '../../components/TopTabStrip';
import { PointsBadge, BadgeDrawer } from '../../components/IhsanPoints';
import PointsToast from '../../components/PointsToast';
import FaithJourney from '../FaithJourney';
import { myEngagements } from '../../data/mockData';
import { getMySignups, refreshVmsStatus } from '../../lib/volunteer';
import { getMyEnrolments } from '../../lib/learn';
import { getMyProfile } from '../../lib/pass';
import MyVolunteering from '../../components/MyVolunteering';
import MyPasses from '../../components/MyPasses';
import BenefitsQrCard from '../../components/BenefitsQrCard';
import CountUpStat from '../../components/CountUpStat';
import { useSession, signOut } from '../../lib/session';
import { requireAuth } from '../../components/AuthGate';

// recharts is heavy; keep it out of the initial bundle since Explore is the
// landing tab and never renders a chart.
const MeImpact = React.lazy(() => import('../../components/MeImpact'));

const ENGAGEMENT_ICON = { 'hand-coins': HandCoins, heart: Heart };

const ME_HERO_IMG = '/images/hero-slide-3-ihsan-points.jpeg';

const SectionLabel = ({ children }) => (
  <p className="px-4 pt-5 pb-2 text-[12px] uppercase tracking-wider font-semibold text-muted-foreground">
    {children}
  </p>
);

const ProgressBar = ({ value, className = 'bg-primary' }) => (
  <div
    className="relative h-2 w-full rounded-full bg-muted overflow-hidden"
    role="progressbar"
    aria-valuenow={value}
    aria-valuemin={0}
    aria-valuemax={100}
  >
    <div
      className={`h-full ${className} rounded-full transition-all duration-500`}
      style={{ width: `${value}%` }}
    />
  </div>
);

const Avatar = ({ initials, bg = 'bg-primary', text = 'text-primary-foreground' }) => (
  <div className={`w-14 h-14 rounded-full ${bg} ${text} flex items-center justify-center font-bold text-lg flex-shrink-0`}>
    {initials}
  </div>
);

// ---------- Reset Demo Data ----------
// This prototype's only persisted state is localStorage (saved items). Everything else
// (points, streaks, badges) lives in in-memory React state seeded with mock defaults, so a
// full reload naturally resets it. This gives testers/stakeholders a clean-slate button
// between sessions without needing a real backend/reset endpoint.
// Sign-in persists once granted, so the gate only reappears after signing out.
// Testers need that switch in reach to walk stakeholders through the flow.
const ResetDemoDataButton = () => {
  const session = useSession();
  return (
    <div className="px-4 pt-2 pb-6 flex flex-col items-center gap-2">
      {session && (
        <button
          type="button"
          data-testid="sign-out"
          onClick={() => { signOut(); centerToast('Signed out', '👋'); }}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign out
        </button>
      )}
      <button
        type="button"
        data-testid="reset-demo-data"
        onClick={() => {
          if (window.confirm('Reset all demo data (sign-in, saved items, points, streaks) and reload?')) {
            localStorage.clear();
            window.location.reload();
          }
        }}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <RotateCcw className="h-3.5 w-3.5" />
        Reset demo data
      </button>
    </div>
  );
};

const NudgeCard = ({ children, testId }) => (
  <div className="px-4 pt-6 pb-2" data-testid={testId}>
    <div className="border-2 border-dashed rounded-xl p-4 bg-amber-50 flex items-start gap-3" style={{ borderColor: '#D4A843' }}>
      <span className="text-lg" aria-hidden="true">💡</span>
      <p className="flex-1 text-sm text-foreground leading-relaxed">{children}</p>
    </div>
  </div>
);

// ---------- "All" summary — no persona gating, just an overview of everything ----------
const AllSummaryView = ({ onEarnPoints, onOpenFaith, onJumpToTab, onOpenRewards }) => {
  const session = useSession();
  // Real Ihsan Points balance from the backend; 0 while loading/not signed in.
  const [points, setPoints] = useState(0);

  useEffect(() => {
    if (!session) { setPoints(0); return undefined; }
    let live = true;
    (async () => {
      const profile = await getMyProfile();
      if (live && profile && typeof profile.points === 'number') setPoints(profile.points);
    })();
    return () => { live = false; };
  }, [session]);

  const benefits = [
    { name: 'Ihsan Aid',              status: 'Active',       tone: 'bg-primary' },
    { name: 'Bursary Application',    status: 'Under review', tone: 'bg-amber-500' },
    { name: 'Kidney Care subsidy',    status: 'Eligible',     tone: 'bg-primary' }
  ];
  const missions = [
    { title: 'Complete your profile',      progress: 80, reward: '+25 pts', cta: 'Complete step', onClickPoints: 25, onClickMsg: 'Profile step completed!' },
    { title: 'Attend 1 Ulum Ihsan class',  progress: 0,  reward: '+50 pts', cta: 'Start', onClickTab: 'learn' },
    { title: 'Refer a neighbour',          progress: 0,  reward: '+50 pts', cta: 'Share link', onClickShare: true }
  ];
  const quickStats = [
    { label: 'Volunteer hours',   value: 22, tab: 'volunteer' },
    { label: 'Classes attended',  value: 6,  tab: 'learn' },
    { label: 'Total given',       value: 50, prefix: '$', tab: 'give' }
  ];

  return (
    <>
      {/* Header card */}
      <div className="px-4 pt-4">
        <Card style={{ backgroundColor: '#E8F5E9' }} className="border-primary/20">
          {session ? (
          <CardContent className="p-4 flex items-center gap-4">
            <Avatar initials={(session.name || 'Member').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'M'} bg="bg-[#1B5E20]" text="text-white" />
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-lg leading-tight">{session.name}</h2>
              <p className="text-sm text-muted-foreground">IhsanOne Member</p>
            </div>
            {/* The points pill left the top bar when the QR button took that
                slot, so this is now the one place points are reachable. */}
            <button
              type="button"
              data-testid="me-points"
              onClick={onOpenRewards}
              aria-label={`Ihsan Points, ${points} available. See what you can get.`}
              className="rounded-full transition-transform active:scale-95"
            >
              <PointsBadge points={points} />
            </button>
          </CardContent>
          ) : (
          <CardContent className="p-4 flex items-center gap-4">
            <Avatar initials="IO" bg="bg-[#1B5E20]" text="text-white" />
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-lg leading-tight">Join IhsanOne</h2>
              <p className="text-sm text-muted-foreground">Sign in to give, learn, volunteer and track your impact.</p>
            </div>
            <Button data-testid="me-signin" size="sm" className="shrink-0" onClick={() => requireAuth()}>Sign in</Button>
          </CardContent>
          )}
        </Card>
      </div>

      {/* QR access */}
      <div className="px-4 pt-4">
        <BenefitsQrCard />
      </div>

      {/* My passes / e-certificates — same self-contained "renders nothing if
          empty" embedding pattern as MyVolunteering below, just not scoped to
          a single top-tab since a pass can belong to any program category. */}
      <MyPasses />

      {/* Quick stats — tap through to that top-tab's activity */}
      <SectionLabel>My activity</SectionLabel>
      <div className="px-4 grid grid-cols-3 gap-2">
        {quickStats.map((s) => (
          <Card
            key={s.label}
            data-testid={`my-activity-${s.tab}`}
            className="cursor-pointer card-hover"
            onClick={() => onJumpToTab(s.tab)}
          >
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-primary">
                <CountUpStat value={s.value} prefix={s.prefix || ''} />
              </p>
              <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <SectionLabel>My impact</SectionLabel>
      <React.Suspense
        fallback={<div className="px-4 h-[196px] md:h-[212px] rounded-xl bg-muted/50 animate-pulse" />}
      >
        <MeImpact />
      </React.Suspense>

      {/* Active benefits */}
      <SectionLabel>Active benefits</SectionLabel>
      <div className="px-4 space-y-2">
        {benefits.map((b) => (
          <Card key={b.name}>
            <CardContent className="p-3 flex items-center gap-3">
              <span className={`w-2 h-2 rounded-full ${b.tone}`} />
              <span className="flex-1 text-sm font-medium">{b.name}</span>
              <span className="text-xs text-muted-foreground">{b.status}</span>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Faith Journey entry card */}
      <SectionLabel>Faith Journey</SectionLabel>
      <div className="px-4">
        <Card className="border-l-4 border-l-primary" data-testid="faith-journey-card">
          <CardContent className="p-4 space-y-3">
            <div>
              <h3 className="font-semibold text-base">Faith Journey 📿</h3>
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                Log your spiritual milestones and earn Ihsan Points
              </p>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-medium">Day 12 of 30 Quran streak</p>
              <ProgressBar value={40} />
            </div>
            <Button
              data-testid="open-faith-journey"
              onClick={onOpenFaith}
              className="w-full h-10 text-sm"
            >
              Open Faith Journey →
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Missions */}
      <SectionLabel>Progress missions</SectionLabel>
      <div className="px-4 space-y-3">
        {missions.map((m, i) => (
          <Card key={i} data-testid={`mission-${i}`}>
            <CardContent className="p-4 space-y-2.5">
              <div className="flex items-start justify-between gap-3">
                <p className="font-semibold text-sm leading-tight">{m.title}</p>
                <span className="text-xs font-bold text-primary whitespace-nowrap">{m.reward}</span>
              </div>
              <ProgressBar value={m.progress} />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{m.progress}% complete</span>
                {m.cta && (
                  <Button
                    size="sm"
                    className="h-7 px-3 text-xs"
                    onClick={() => {
                      if (m.onClickPoints) {
                        onEarnPoints?.(m.onClickPoints, m.onClickMsg);
                      } else if (m.onClickTab) {
                        onJumpToTab?.(m.onClickTab);
                      } else if (m.onClickShare) {
                        navigator.clipboard?.writeText(`${window.location.origin}/?ref=ihsanone-demo`);
                        centerToast('Referral link copied', '🔗');
                      }
                    }}
                  >
                    {m.cta}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <NudgeCard testId="nudge-all">
        You&apos;ve earned <span className="font-semibold">{points} pts</span>. Learn, volunteer or give to unlock more badges and rewards.
      </NudgeCard>

      <ResetDemoDataButton />
    </>
  );
};

// ---------- Engagement list for Services / Learn / Volunteer / Give tabs ----------
const EngagementList = ({ items, emptyLabel }) => (
  <div className="px-4 pt-4 pb-8 space-y-3" data-testid="engagement-list">
    {items.length === 0 && (
      <div className="text-center py-16 text-sm text-muted-foreground" data-testid="engagement-empty">
        {emptyLabel}
      </div>
    )}
    {items.map((item) => {
      const Icon = ENGAGEMENT_ICON[item.icon] || HandCoins;
      return (
        <Card key={item.id} data-testid={`engagement-card-${item.id}`}>
          <CardContent className="p-3 flex items-center gap-3">
            {item.image ? (
              <img src={item.image} alt={item.title} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Icon className="h-5 w-5" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0F172A] truncate">{item.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item.meta}</p>
            </div>
            <span className="text-[11px] font-semibold text-primary whitespace-nowrap flex items-center gap-1">
              {['Approved', 'Confirmed', 'Accepted', 'Completed'].includes(item.status) ? <CheckCircle2 className="h-3.5 w-3.5" /> : null}
              {item.status}
            </span>
          </CardContent>
        </Card>
      );
    })}
  </div>
);

const ENGAGEMENT_EMPTY_LABEL = {
  services: "You haven't applied for any assistance yet.",
  learn: "You're not enrolled in any classes yet — explore Learn to get started.",
  volunteer: "You haven't signed up for any volunteer opportunities yet.",
  give: "You haven't given to any campaigns yet."
};

// ---------- MAIN ----------
export default function MyIhsan({ onNavigate, onTopTabChange, onOpenRewards, initialMeTab = 'all' }) {
  const [badgesOpen, setBadgesOpen] = useState(false);
  const [activePage, setActivePage] = useState('home'); // 'home' | 'faith-journey'
  const [pointsToast, setPointsToast] = useState({ visible: false, points: 0, action: '' });
  // Me keeps its own top-tab so Explore/Saved/Updates never leak their last
  // filter in. SuperApp remounts this page on every visit, which resets it —
  // to All normally, or to whichever segment the caller asked for.
  const [meTab, setMeTab] = useState(initialMeTab);
  // Volunteer engagements come from the real backend (services + give are still mock).
  const [volunteerItems, setVolunteerItems] = useState(null); // null = loading
  // Learn enrolments come from the real backend too — the whole point of the
  // Learn build is that "Enrolled" surfaces here without hand-waving.
  const [learnItems, setLearnItems] = useState(null);

  useEffect(() => {
    if (meTab !== 'volunteer') return;
    let live = true;
    setVolunteerItems(null);
    (async () => {
      const rows = await getMySignups();
      if (!live) return;
      const items = rows.map((r) => ({
        id: r.listing_id,
        title: r.listing_title || 'Volunteer opportunity',
        meta: `Applied · ${new Date(r.created_at).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })}`,
        // Real lifecycle status from the VMS (cached locally as vms_status).
        status: r.vms_status || 'Confirmed',
        _appId: r.vms_application_id,
      }));
      setVolunteerItems(items);
      // Refresh each from the VMS so a coordinator's Accept/Reject shows here.
      const refreshed = await Promise.all(items.map(async (it) => {
        if (!it._appId) return it;
        const s = await refreshVmsStatus(it._appId);
        return s ? { ...it, status: s } : it;
      }));
      if (live) setVolunteerItems(refreshed);
    })();
    return () => { live = false; };
  }, [meTab]);

  useEffect(() => {
    if (meTab !== 'learn') return;
    let live = true;
    setLearnItems(null);
    (async () => {
      const rows = await getMyEnrolments();
      if (!live) return;
      setLearnItems(rows.map((r) => ({
        id: r.course_id,
        title: r.course_title || 'Course',
        meta: `Enrolled · ${new Date(r.created_at).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })}`,
        status: 'Enrolled',
      })));
    })();
    return () => { live = false; };
  }, [meTab]);

  const celebratePoints = (points, action) => {
    setPointsToast({ visible: true, points, action });
  };

  if (activePage === 'faith-journey') {
    return <FaithJourney onBack={() => setActivePage('home')} onNavigate={onNavigate} />;
  }

  return (
    <div className="bg-background page-fade-in pb-6" data-testid="me-page">
      <TopTabStrip activeTab={meTab} onChange={setMeTab} />

      {meTab === 'all' && (
        <>
          {/* Hero — same rounded card treatment as the Services/Learn/Volunteer hubs */}
          <div className="p-4 desktop-shell md:pt-6">
            <div
              className="relative rounded-2xl overflow-hidden h-[150px] md:h-[220px]"
              data-testid="me-hero"
              style={{ backgroundColor: '#0F9488' }}
            >
              <div className="absolute inset-0 flex items-center">
                <div className="max-w-[55%] pl-5 md:pl-10 z-10">
                  <h2 className="text-2xl md:text-3xl font-extrabold text-white leading-tight">My Ihsan</h2>
                  <p className="text-xs md:text-base text-white/80 mt-1.5 md:mt-2.5 leading-snug">
                    Your personal access to IhsanOne.
                  </p>
                </div>
                <img
                  src={ME_HERO_IMG}
                  alt=""
                  className="absolute right-0 bottom-0 h-full w-[55%] object-cover object-bottom"
                />
              </div>
            </div>
          </div>

          {/* View all badges trigger */}
          <div className="px-4 pt-4 desktop-shell">
            <button
              type="button"
              data-testid="open-badges"
              onClick={() => setBadgesOpen(true)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-[#D4A843]/40 hover:bg-amber-100 transition-colors"
            >
              <Medal className="h-4 w-4 text-[#D4A843] flex-shrink-0" />
              <span className="text-sm font-semibold flex-1 text-left">View all badges</span>
              <span className="text-xs text-muted-foreground">See what you&apos;ve earned →</span>
            </button>
          </div>

          <div className="desktop-shell">
            <AllSummaryView
              onEarnPoints={celebratePoints}
              onOpenFaith={() => setActivePage('faith-journey')}
              onOpenRewards={onOpenRewards}
              onJumpToTab={(tab) => {
                // Navigating to Explore resets it to Categories, so claim the
                // sub-tab afterwards to land on the hub the stat refers to.
                onNavigate?.('explore');
                onTopTabChange?.(tab);
              }}
            />
          </div>
        </>
      )}

      {meTab !== 'all' && (
        <div className="desktop-shell">
          {meTab === 'volunteer' && <MyVolunteering />}
          {meTab === 'volunteer' && volunteerItems === null ? (
            <div className="text-center py-16 text-sm text-muted-foreground">Loading your sign-ups…</div>
          ) : meTab === 'learn' && learnItems === null ? (
            <div className="text-center py-16 text-sm text-muted-foreground">Loading your enrolments…</div>
          ) : (
            <EngagementList
              items={
                meTab === 'volunteer' ? volunteerItems
                  : meTab === 'learn' ? learnItems
                  : (myEngagements[meTab] || [])
              }
              emptyLabel={ENGAGEMENT_EMPTY_LABEL[meTab]}
            />
          )}
        </div>
      )}

      <BadgeDrawer isOpen={badgesOpen} onClose={() => setBadgesOpen(false)} />

      <PointsToast
        isVisible={pointsToast.visible}
        points={pointsToast.points}
        action={pointsToast.action}
        onDismiss={() => setPointsToast((p) => ({ ...p, visible: false }))}
      />
    </div>
  );
}
